<script setup lang="ts">
import { IonContent, IonFooter, IonIcon, IonRefresher, IonRefresherContent, RefresherCustomEvent, IonSpinner } from '@ionic/vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import NavigationBar from '@/shared/components/ui/NavigationBar.vue';
import TopBar from '@/shared/components/TopBar.vue';
import useSafety from '@/modules/safety/composables/useSafety';
import BackButton from '@/shared/components/BackButton.vue';

const { t } = useI18n();
const route = useRoute();
const incidentId = Number(route.params.id);

const { getReport } = useSafety();
const { report, isFetchingReport, refetchReport } = getReport(incidentId);

const onRefresh = async (ev: RefresherCustomEvent) => {
	await refetchReport();
	ev.target.complete();
};

const formatDate = (iso: string) => new Date(iso).toLocaleString();
</script>

<template>
	<ion-content>
		<ion-refresher class="mt-8" slot="fixed" @ionRefresh="onRefresh">
			<ion-refresher-content />
		</ion-refresher>

		<TopBar class="px-[34px]">
			<template #left>
				<BackButton to="/safety/my-reports" />
			</template>
			<template #center>
				<div class="flex justify-center w-full font-sourcesanspro-semibold text-l2">
					{{ t('safety.my_reports') }}
				</div>
			</template>
		</TopBar>

		<div class="px-[34px] pb-[120px] w-full max-w-[600px] mx-auto">
			<div v-if="isFetchingReport && !report" class="flex justify-center py-10">
				<ion-spinner name="crescent" />
			</div>

			<div v-else-if="!report" class="text-center text-white/70 py-10 font-sourcesanspro-regular">
				{{ t('safety.report_not_found') }}
			</div>

			<div v-else class="text-white space-y-4">
				<div class="rounded backdrop-blur-xl bg-white/10 p-4">
					<div class="text-xs2 text-white/60 uppercase tracking-wide font-sourcesanspro-semibold">{{ t(`safety.types.${report.type}`) }}</div>
					<div class="text-l2 font-sourcesanspro-semibold mt-1">#{{ report.public_code }}</div>
					<div class="text-sm2 font-sourcesanspro-regular mt-2">
						{{ t(`safety.status.${report.status}`) }}
						<span v-if="report.priority === 'p1'" class="ml-2 px-2 py-0.5 rounded bg-red-600 text-white text-xs2 font-sourcesanspro-semibold">P1</span>
					</div>
					<div v-if="report.description" class="mt-3 text-white/90 font-sourcesanspro-regular text-sm2">{{ report.description }}</div>
				</div>

				<div v-if="(report.timeline?.length ?? 0) > 0" class="rounded backdrop-blur-xl bg-white/10 p-4">
					<div class="font-sourcesanspro-semibold text-base2 mb-3">{{ t('safety.timeline') }}</div>
					<ol class="space-y-3 border-l border-white/20 pl-4">
						<li v-for="(event, idx) in report.timeline ?? []" :key="idx">
							<div class="text-xs2 text-white/60 font-sourcesanspro-regular">{{ formatDate(event.created_at) }}</div>
							<div class="text-sm2 font-sourcesanspro-semibold">{{ t(`safety.timeline_kind.${event.kind}`) }}</div>
						</li>
					</ol>
				</div>
			</div>
		</div>
	</ion-content>

	<ion-footer>
		<NavigationBar item-active="safety" />
	</ion-footer>
</template>
